/**
 * Feature flags — read from the VITE_FEATURE_FLAGS env variable.
 *
 * VITE_FEATURE_FLAGS is a JSON string, e.g.:
 *   '{"enable_ebra":true,"show_remarks":true}'
 */

const parsedFlags: Record<string, boolean> = (() => {
  try {
    return JSON.parse(import.meta.env.VITE_FEATURE_FLAGS || '{}');
  } catch {
    console.error('Failed to parse VITE_FEATURE_FLAGS');
    return {};
  }
})();

export const FEATURE_FLAGS = {
  SHOW_REMARKS: 'show_remarks',
  SHOW_TAGS: 'show_tags',
  USE_CHECK_API_V2: 'use_check_api_v2',
  EDIT_AGENT_PROFILE: 'edit_agent_profile',
  PACKAGE_VIEW_TOGGLE: 'package_view_toggle',
  MULTI_SELECT_SUBMIT_FOR_REVIEW: 'multi_select_submit_for_review',
  ENABLE_FALL_THROUGH_WORKFLOW: 'enable_fall_through_workflow',
  ENABLE_PURCHASE_FALL_THROUGH_WORKFLOW:
    'enable_purchase_fall_through_workflow',
  LOCK_EXISTING_DOCUSEAL_FIELDS: 'lock_existing_docuseal_fields',
  ENABLE_REMARKS_INBOX: 'enable_remarks_inbox',
  ENABLE_RESYNC_TRANSACTION: 'enable_resync_transaction',
  INHOUSE_SIGNING: 'inhouse_signing',
  ENABLE_MULTI_OFFER_UI: 'enable_multi_offer_ui',
  // Master switch for the CASL/RBAC rollout (transaction sharing, viewer read-only,
  // scoped lists). Off = pre-CASL behavior; on = new functionality.
  RBAC_ENABLED: 'rbac_enabled',
  // Lets uploaded (template-less) documents be submitted for Max review from the
  // document view, matching the bulk action on the documents list.
  UPLOADED_DOC_MAX_REVIEW: 'uploaded_doc_max_review',
  // Deep link back to the transaction in agent-facing emails (rectification,
  // check/earnestly notifications). Also gated per-partner via
  // showTransactionLinkInAgentEmails in partnerConfig.
  ENABLE_TRANSACTION_LINK_IN_AGENT_EMAILS:
    'enable_transaction_link_in_agent_emails',
  // The Daily Hub v2: the hub itself, the redesigned document viewer and the full-screen task
  // detail. One switch because the three hand work to each other — a redesigned hub opening a
  // classic viewer breaks in both directions — so they cannot be turned on piecemeal.
  //
  // There is no user escape hatch: no stored preference and no ?v= override, so rolling a brokerage
  // back means turning this off or clearing its partner opt-in, not asking users to switch. Agent is
  // unaffected throughout — it renders verify-document-v2, which this never touches.
  //
  // Both this and the checklist below are half a gate: the partner must opt in too, so one
  // environment variable cannot switch a brokerage over by itself.
  DAILY_HUB_V2: 'daily_hub_v2',
  // The documents checklist replacing Required Docs on the compliance transaction page. Separate
  // from the redesign because it is the one piece that can ship on its own. It does not gate the
  // checklist inside the Daily Hub — there the checklist IS the hub's own UI, and the section it
  // replaced never shipped.
  DOCUMENT_CHECKLIST: 'document_checklist',
  CHECKS_DASHBOARD_V2: 'checks_dashboard_v2',
  // Brand switcher, brand logos and the active-brand header. Pairs with the
  // backend's FEATURE_FLAG_BRAND_ENABLED: the profile returns brands as soon as a
  // partner has Brand rows, before the backend scopes anything by them.
  BRAND_ENABLED: 'brand_enabled',
  // Mounts the Ask Max panel (AskMaxHost)
  ASK_MAX: 'ask_max',
} satisfies Record<string, string>;

export type FeatureFlagName =
  (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

// Reads the environment only. Flags listed in PARTNER_GATED (useFeatureFlag.ts) additionally need
// the partner's opt-in, which this cannot see — read those through useFeatureFlag, not here.
export function getFeatureFlag(name: FeatureFlagName): boolean {
  return parsedFlags[name] === true;
}

const featureFlagEnvironment = {};

export default featureFlagEnvironment;
